// src/components/Organizer/Register/OrganizerRegisterForm.tsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useOrganizer } from "../../../contexts/OrganizerContext";
import { useAuth } from "../../../contexts/AuthContext";
import axios from "axios";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import { getAuthToken } from "../../../utils/tokenStorage";
import {
	getAccountLoginUrl,
	getAccountRegisterUrl,
} from "../../../utils/accountApp";
import {
	createEmptySocialLinkState,
	SocialLinkState,
	SocialPlatformKey,
} from "../../../constants/socialPlatforms";
import { FiUser, FiCreditCard, FiCheckCircle, FiArrowLeft, FiClipboard } from "react-icons/fi";

const STORAGE_KEY = "blackadder:organizer_register_draft";

interface FormData {
	organizerType: string;
	organizerName: string;
	representativeName: string;
	email: string;
	phone: string;
	website: string;
	description: string;
	bankName: string;
	branchName: string;
	accountType: string;
	accountNumber: string;
	accountHolderName: string;
	agreeToTerms: boolean;
}

const OrganizerRegisterForm: React.FC = () => {
	const navigate = useNavigate();
	const { refreshOrganizerData } = useOrganizer();
	const { isAuthenticated, hasFetched } = useAuth();
	const [step, setStep] = useState(1);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formData, setFormData] = useState<FormData>({
		organizerType: "",
		organizerName: "",
		representativeName: "",
		email: "",
		phone: "",
		website: "",
		description: "",
		bankName: "",
		branchName: "",
		accountType: "",
		accountNumber: "",
		accountHolderName: "",
		agreeToTerms: false,
	});
	const [socialLinks, setSocialLinks] = useState<SocialLinkState>(
		createEmptySocialLinkState()
	);

	// localStorage に下書きがある場合はローディング表示
	const [isRestoring, setIsRestoring] = useState(() => {
		return !!localStorage.getItem(STORAGE_KEY);
	});
	const restoredRef = useRef(false);

	// 認証チェック完了後に localStorage から復元
	useEffect(() => {
		if (!hasFetched || restoredRef.current) return;

		const draft = localStorage.getItem(STORAGE_KEY);
		if (!draft) {
			setIsRestoring(false);
			return;
		}

		try {
			const parsed = JSON.parse(draft);
			// 24時間以上経過した下書きは破棄
			if (Date.now() - parsed.timestamp > 24 * 60 * 60 * 1000) {
				localStorage.removeItem(STORAGE_KEY);
				setIsRestoring(false);
				return;
			}

			restoredRef.current = true;
			setFormData({ ...parsed.formData, agreeToTerms: false });
			setSocialLinks(parsed.socialLinks);

			if (isAuthenticated) {
				setStep(3);
				localStorage.removeItem(STORAGE_KEY);
			}
		} catch {
			localStorage.removeItem(STORAGE_KEY);
		}
		setIsRestoring(false);
	}, [hasFetched, isAuthenticated]);

	const handleChange = (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
		>
	) => {
		const { name, value, type } = e.target;
		if (type === "checkbox") {
			const checked = (e.target as HTMLInputElement).checked;
			setFormData((prev) => ({ ...prev, [name]: checked }));
		} else {
			setFormData((prev) => ({ ...prev, [name]: value }));
		}
	};

	const nextStep = () => setStep((prev) => prev + 1);
	const prevStep = () => setStep((prev) => prev - 1);

	const handleSocialLinkChange = (
		platform: SocialPlatformKey,
		value: string
	) => {
		setSocialLinks((prev) => ({
			...prev,
			[platform]: value,
		}));
	};

	// フォームデータを localStorage に保存してリダイレクト
	const saveAndRedirect = useCallback(
		(redirectUrl: string) => {
			const draft = {
				formData,
				socialLinks,
				timestamp: Date.now(),
			};
			localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));

			const currentUrl = window.location.href;
			window.location.href = `${redirectUrl}?redirect=${encodeURIComponent(
				currentUrl
			)}`;
		},
		[formData, socialLinks]
	);

	const handleLoginRedirect = useCallback(() => {
		saveAndRedirect(getAccountLoginUrl());
	}, [saveAndRedirect]);

	const handleRegisterRedirect = useCallback(() => {
		saveAndRedirect(getAccountRegisterUrl());
	}, [saveAndRedirect]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!isAuthenticated) {
			handleLoginRedirect();
			return;
		}

		setIsSubmitting(true);

		const API_BASE =
			process.env.REACT_APP_API_BASE ||
			(window.location.hostname.includes("local")
				? "https://api.blackadder.local:8890/api"
				: "https://api.blackadder.co.jp/api");

		try {
			// API リクエストの設定を作成
			const config = {
				withCredentials: true,
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				} as Record<string, string>,
			};

			const token = getAuthToken();

			if (token) {
				config.headers["Authorization"] = `Bearer ${token}`;
			}

			const formattedSocialLinks = Object.entries(socialLinks)
				.filter(([, value]) => value.trim() !== "")
				.map(([platform, value]) => ({
					platform,
					value: value.trim(),
				}));

			// 主催者登録APIを呼び出し
			await axios.post(
				`${API_BASE}/organizer/register`,
				{
					organizerType: formData.organizerType,
					organizerName: formData.organizerName,
					representativeName: formData.representativeName,
					email: formData.email,
					phone: formData.phone,
					website: formData.website,
					description: formData.description,
					bankName: formData.bankName,
					branchName: formData.branchName,
					accountType: formData.accountType,
					accountNumber: formData.accountNumber,
					accountHolderName: formData.accountHolderName,
					socialLinks: formattedSocialLinks,
				},
				config
			);

			// 主催者情報を再取得
			await refreshOrganizerData();

			alert(
				"主催者登録申請を受け付けました。審査後、メールでご連絡いたします。"
			);
			navigate("/organizer");
		} catch (error) {
			console.error("Registration error:", error);
			alert("登録中にエラーが発生しました");
		} finally {
			setIsSubmitting(false);
		}
	};

	// ステップ情報
	const steps = [
		{ index: 1, label: "基本情報", icon: FiUser },
		{ index: 2, label: "口座情報", icon: FiCreditCard },
		{ index: 3, label: "確認", icon: FiCheckCircle },
	];

	// localStorage からの復元待ち
	if (isRestoring) {
		return (
			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 flex justify-center items-center min-h-[200px]">
					<div className="flex flex-col items-center space-y-3">
						<div className="animate-spin h-8 w-8 border-3 border-violet-500 border-t-transparent rounded-full" />
						<p className="text-sm text-gray-600 dark:text-gray-400">
							入力データを復元中...
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8">
				<Link
					to="/"
					className="inline-flex items-center text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 mb-4"
				>
					<FiArrowLeft className="w-4 h-4 mr-1" />
					トップページに戻る
				</Link>
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
					<div className="flex items-center gap-4 mb-4">
						<div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full">
							<FiClipboard className="w-6 h-6 text-white" />
						</div>
						<div>
							<h1 className="text-3xl font-bold text-gray-900 dark:text-white">
								主催者登録
							</h1>
							<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
								イベント主催者としての情報を登録してください
							</p>
						</div>
					</div>
				</div>
			</div>

			<div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">

				{/* Stepper */}
				<div
					data-hs-stepper={JSON.stringify({ currentIndex: step })}
					className="mb-8"
				>
					{/* Stepper Nav */}
					<ul className="relative flex flex-row justify-center max-w-2xl mx-auto w-full pt-2 px-2">
						{steps.map((s) => {
							const IconComponent = s.icon;
							const isActive = s.index === step;
							const isCompleted = s.index < step;

							return (
								<li
									key={s.index}
									className="flex items-center gap-x-2 shrink group"
									data-hs-stepper-nav-item={JSON.stringify({
										index: s.index,
										isCompleted: isCompleted,
									})}
								>
									<div className="flex flex-col items-center">
										<div className="flex items-center">
											<div className="flex flex-col items-center flex-shrink-0">
												<div
													className={`relative flex justify-center items-center w-12 h-12 rounded-full font-medium transition-all duration-300 ${
														isActive
															? "bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg scale-110"
															: isCompleted
															? "bg-gradient-to-r from-violet-500 to-purple-600 text-white"
															: "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
													}`}
												>
													{isCompleted ? (
														<svg
															xmlns="http://www.w3.org/2000/svg"
															width="24"
															height="24"
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															strokeWidth="3"
															strokeLinecap="round"
															strokeLinejoin="round"
															className="w-5 h-5"
														>
															<polyline points="20 6 9 17 4 12"></polyline>
														</svg>
													) : (
														<IconComponent className="w-5 h-5" />
													)}
													{isActive && (
														<div className="absolute inset-0 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 opacity-30 animate-ping"></div>
													)}
												</div>
												<span
													className={`mt-2 text-xs font-medium transition-colors whitespace-nowrap ${
														isActive || isCompleted
															? "text-violet-600 dark:text-violet-400"
															: "text-gray-600 dark:text-gray-400"
													}`}
												>
													{s.label}
												</span>
											</div>
											{s.index !== steps.length && (
												<div className="w-12 sm:w-24 h-0.5 mx-1 sm:mx-2 mb-6 relative">
													<div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 rounded-full" />
													<div
														className={`absolute inset-0 rounded-full transition-all duration-500 ${
															isCompleted
																? "bg-gradient-to-r from-violet-500 to-purple-600 w-full"
																: "w-0"
														}`}
													/>
												</div>
											)}
										</div>
									</div>
								</li>
							);
						})}
					</ul>
					{/* End Stepper Nav */}

					{/* Stepper Content */}
					<div className="mt-5 sm:mt-8">
						{step === 1 && (
							<div
								data-hs-stepper-content-item={JSON.stringify({
									index: 1,
									isCompleted: step > 1,
								})}
								className="active"
							>
								<Step1
									formData={formData}
									socialLinks={socialLinks}
									handleChange={handleChange}
									onSocialLinkChange={handleSocialLinkChange}
									nextStep={nextStep}
								/>
							</div>
						)}
						{step === 2 && (
							<div
								data-hs-stepper-content-item={JSON.stringify({ index: 2 })}
								className="active"
							>
								<Step2
									formData={formData}
									handleChange={handleChange}
									nextStep={nextStep}
									prevStep={prevStep}
								/>
							</div>
						)}
						{step === 3 && (
							<div
								data-hs-stepper-content-item={JSON.stringify({ index: 3 })}
								className="active"
							>
								<Step3
									formData={formData}
									socialLinks={socialLinks}
									handleChange={handleChange}
									prevStep={prevStep}
									isSubmitting={isSubmitting}
									onSubmit={handleSubmit}
									isAuthenticated={isAuthenticated}
									onLoginRedirect={handleLoginRedirect}
									onRegisterRedirect={handleRegisterRedirect}
								/>
							</div>
						)}
					</div>
					{/* End Stepper Content */}
				</div>
				{/* End Stepper */}
			</div>
		</div>
	);
};

export default OrganizerRegisterForm;
